/**
 * Botón / enlace "Ver en Google Maps" para una sede.
 *
 * Recibe el campo `enlaceMapa` del CMS (URL completa que el equipo ya pegó
 * desde Google Maps al dar de alta la sede) y lo abre en una pestaña nueva
 * con `rel="noopener noreferrer"`. Si la sede no tiene enlace, no renderiza
 * nada — así el componente padre no necesita comprobar nada antes.
 *
 * Variantes visuales:
 *   - "button" (default): píldora con borde + icono, para listados de sedes.
 *   - "inline": texto + icono pequeño, para asides compactos.
 */
interface SedeMapaLinkProps {
  enlaceMapa?: string;
  ciudad: string;
  variant?: "button" | "inline";
}

export function SedeMapaLink({ enlaceMapa, ciudad, variant = "button" }: SedeMapaLinkProps) {
  if (!enlaceMapa) return null;

  const ariaLabel = `Ver ${ciudad} en Google Maps (abre en una pestaña nueva)`;

  if (variant === "inline") {
    return (
      <a
        href={enlaceMapa}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={ariaLabel}
        className="inline-flex items-center gap-1 text-sm font-semibold text-brand-orange-deep hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2"
      >
        <MapPinIcon />
        Cómo llegar
      </a>
    );
  }

  return (
    <a
      href={enlaceMapa}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-brand-ink transition hover:border-brand-orange hover:text-brand-orange-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2"
    >
      <MapPinIcon />
      Ver en Google Maps
    </a>
  );
}

function MapPinIcon() {
  // Icono inline para no añadir dependencia de lucide-react aquí.
  // 16x16, hereda el `currentColor` del enlace contenedor.
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
